import React from 'react';
import { AlertTriangle, Calendar, Users, Mail, FileText, Sparkles } from 'lucide-react';

const SEVERITY_CLS = {
  warn: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-sky-50 border-sky-200 text-sky-900',
  ok:   'bg-emerald-50 border-emerald-200 text-emerald-900',
};

const SectionIcon = ({ name }) => {
  const Icon = {
    members:        Users,
    events:         Calendar,
    blog:           FileText,
    communications: Mail,
  }[name] || Sparkles;
  return <Icon className="w-4 h-4" />;
};

/**
 * BriefingPanel — compact "today" view surfaced at the top of the
 * AI Agent page. Each section is permission-scoped to the user by the
 * backend.
 */
export const BriefingPanel = ({ data, onAsk }) => {
  if (!data) return null;
  const headline = data.headline || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="ai-briefing-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#002249]">Daily Briefing</div>
          <div className="text-xs text-slate-500 mt-0.5">{data.scope || 'Personal briefing'}</div>
        </div>
      </div>

      {headline.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          {headline.map((h, i) => (
            <div
              key={i}
              className={`rounded-xl border px-3 py-2.5 ${SEVERITY_CLS[h.severity] || SEVERITY_CLS.info}`}
              data-testid={`ai-briefing-headline-${i}`}
            >
              <div className="text-[11px] uppercase font-semibold tracking-wide opacity-80">{h.label}</div>
              <div className="text-2xl font-bold mt-0.5">{h.value?.toLocaleString?.() ?? h.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.members && (
          <BriefingSection
            name="members"
            title="Membership"
            actions={data.members.actions}
            stats={[
              { label: 'Never logged in', value: data.members.unactivated },
              { label: 'Incomplete profiles', value: data.members.incomplete_profiles },
              { label: 'New this week', value: data.members.new_this_week },
            ]}
            onAsk={onAsk}
            askPrompt="Show me the members who never logged in."
          />
        )}
        {data.events && (
          <BriefingSection
            name="events"
            title="Events"
            actions={
              data.events.missing_artwork?.length
                ? [`${data.events.missing_artwork.length} upcoming event(s) missing cover art.`]
                : ['No critical event issues in the next 14 days.']
            }
            stats={[
              { label: 'Upcoming (14d)', value: data.events.upcoming_14_days },
              { label: 'Missing artwork', value: data.events.missing_artwork?.length || 0 },
            ]}
            onAsk={onAsk}
            askPrompt={
              data.events.missing_artwork?.length
                ? `Which upcoming events are missing artwork?`
                : `Show me upcoming events.`
            }
          />
        )}
        {data.blog && (
          <BriefingSection
            name="blog"
            title="Blog & News"
            stats={[
              { label: 'Pending submissions', value: data.blog.pending_submissions },
              { label: 'Drafts', value: data.blog.drafts },
            ]}
            onAsk={onAsk}
            askPrompt="Show me the current blog drafts."
          />
        )}
        {data.communications && (
          <BriefingSection
            name="communications"
            title="Communications"
            stats={[
              { label: 'Scheduled campaigns', value: data.communications.scheduled_campaigns },
            ]}
            actions={
              (data.communications.last_campaigns || []).map(
                (c) => `Last: "${(c.subject || '').slice(0, 50)}" — ${c.sent ?? 0} sent`,
              )
            }
            onAsk={onAsk}
            askPrompt="How did our last campaign perform?"
          />
        )}
      </div>
    </div>
  );
};

const BriefingSection = ({ name, title, stats = [], actions = [], onAsk, askPrompt }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-md bg-[#002249] text-white flex items-center justify-center">
        <SectionIcon name={name} />
      </div>
      <div className="font-bold text-[#1a1a1a] text-[13px]">{title}</div>
    </div>
    <div className="grid grid-cols-3 gap-1.5 mb-2">
      {stats.map((s, i) => (
        <div key={i} className="bg-white rounded-lg px-2 py-1.5 border border-slate-200">
          <div className="text-[9.5px] uppercase font-semibold text-slate-500 leading-tight">{s.label}</div>
          <div className="text-sm font-bold text-[#1a1a1a] mt-0.5">{s.value ?? 0}</div>
        </div>
      ))}
    </div>
    {actions.length > 0 && (
      <ul className="text-[11.5px] text-slate-600 space-y-1 mb-2">
        {actions.slice(0, 3).map((a, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
            <span>{a}</span>
          </li>
        ))}
      </ul>
    )}
    {askPrompt && (
      <button
        onClick={() => onAsk?.(askPrompt)}
        className="text-[11px] text-[#002249] font-semibold hover:underline"
        data-testid={`ai-briefing-ask-${name}`}
      >
        Ask the AI →
      </button>
    )}
  </div>
);

export default BriefingPanel;
